import {
  Box,
  makeStyles,
  Tooltip,
  Typography,
  Theme,
  useTheme,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import classNames from "classnames";
import { addDays, endOfToday } from "date-fns";
import { parseISO } from "date-fns/esm";
import {
  AutocompleteInput,
  BooleanField,
  Datagrid,
  Filter,
  FunctionField,
  Record,
  ReferenceInput,
  TextField,
  useTranslate,
  Responsive,
  SimpleList,
} from "react-admin";
import eldMalfunctions from ".";
import resourceNames from "../../../common/data/resourceNames";
import { EldMalfunctionOrDataDiagnostic } from "../../../common/data/service-proxies";
import { Void } from "../../common/data-grid-fields/Void";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import "../../common/styles/list-style.css";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import { useSearchFields } from "../../../common/hooks/useSearchFields";

const useStyles = makeStyles(() => ({
  centeredContent: {
    display: "block",
  },
}));

const ListFilter = (props: any) => {
  useSearchFields(props, ["name"], resourceNames.ADMIN_VEHICLES);
  return (
    <Filter {...props}>
      <ReferenceInput
        className="list-view-filter"
        source="vehicleId"
        label="resources.hos-eld-malfunctions.labels.vehicleName"
        alwaysOn
        reference={resourceNames.ADMIN_VEHICLES}
      >
        <AutocompleteInput resettable />
      </ReferenceInput>
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        options={{
          autoOk: true,
        }}
        className="list-view-filter"
        label="resources.hos-eld-malfunctions.labels.dateFrom"
        source="dateFrom"
        alwaysOn
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        options={{
          autoOk: true,
        }}
        className="list-view-filter"
        label="resources.hos-eld-malfunctions.labels.dateTo"
        source="dateTo"
        alwaysOn
      />
    </Filter>
  );
};

export const EldMalfunctionsList = (props: any) => {
  const translate = useTranslate();
  const classes = useStyles();
  const theme: Theme = useTheme();
  const renderPrimaryTextForSimpleList = (record: any) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Tooltip
          title={translate("resources.assets-maintenance.labels.vehicleName")}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "4px",
              alignItems: "center",
            }}
          >
            <LocalShippingOutlinedIcon
              style={{
                color: theme.palette.primary.main,
              }}
            />
            <TextField
              source="vehicleName"
              label="resources.hos-eld-malfunctions.labels.vehicleName"
            />
          </div>
        </Tooltip>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Cleared:
          </Typography>
          <BooleanField
            headerClassName="centered-content"
            className={classNames(classes.centeredContent, "centered-content")}
            source="isCleared"
            label="resources.hos-eld-malfunctions.labels.isCleared"
          />
        </div>
      </div>
    );
  };

  const renderSecondaryTextForSimpleList = (record: any) => {
    return (
      <div
        style={{
          marginTop: "14px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Time:
          </Typography>
          <FunctionField<EldMalfunctionOrDataDiagnostic & Record>
            source="time"
            label="resources.hos-eld-malfunctions.labels.time"
            render={(record) =>
              !!record?.time && record?.homeTerminalTimeZone ? (
                <Typography variant="body2">
                  {parseISO(record.time).toLocaleString("en-US", {
                    timeZone: record.homeTerminalTimeZone,
                  })}
                </Typography>
              ) : null
            }
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Malfunction or data diagnostic:
          </Typography>
          <FunctionField<EldMalfunctionOrDataDiagnostic & Record>
            source="malfunctionOrDiagnostic"
            label="resources.hos-eld-malfunctions.labels.malfunctionOrDiagnostic"
            render={(record) =>
              !!record?.malfunctionOrDiagnostic ? (
                <Box flexDirection="column">
                  <Typography variant="body2">
                    {translate(
                      `resources.hos-eld-malfunctions.malfunctions.${record.malfunctionOrDiagnostic}`
                    )}
                  </Typography>
                  <Typography variant="caption">
                    {translate(
                      `resources.hos-eld-malfunctions.malfunctionDescriptions.${record.malfunctionOrDiagnostic}`
                    )}
                  </Typography>
                </Box>
              ) : null
            }
          />
        </div>
      </div>
    );
  };

  return (
    <div className="content-container">
      <DefaultProLogsList
        title="resources.hos-eld-malfunctions.name"
        actions={<Void />}
        filters={<ListFilter />}
        filterDefaultValues={{
          dateFrom: addDays(endOfToday(), -7).toISOString(),
          dateTo: endOfToday().toISOString(),
        }}
        bulkActionButtons={false}
        sort={{ field: "time", order: "DESC" }}
        empty={<ProLogsEmpty iconType={eldMalfunctions.icon} />}
        {...props}
      >
        <Responsive
          small={
            <SimpleList
              className="simple-list-wrapper"
              primaryText={renderPrimaryTextForSimpleList}
              secondaryText={renderSecondaryTextForSimpleList}
              linkType={false}
            />
          }
          medium={
            <Datagrid>
              <TextField
                source="vehicleName"
                label="resources.hos-eld-malfunctions.labels.vehicleName"
              />
              <FunctionField<EldMalfunctionOrDataDiagnostic & Record>
                source="time"
                label="resources.hos-eld-malfunctions.labels.time"
                render={(record) =>
                  !!record?.time && record?.homeTerminalTimeZone ? (
                    <Typography variant="body2">
                      {parseISO(record.time).toLocaleString("en-US", {
                        timeZone: record.homeTerminalTimeZone,
                      })}
                    </Typography>
                  ) : null
                }
              />
              <FunctionField<EldMalfunctionOrDataDiagnostic & Record>
                source="malfunctionOrDiagnostic"
                label="resources.hos-eld-malfunctions.labels.malfunctionOrDiagnostic"
                render={(record) =>
                  !!record?.malfunctionOrDiagnostic ? (
                    <Box flexDirection="column">
                      <Typography variant="body2">
                        {translate(
                          `resources.hos-eld-malfunctions.malfunctions.${record.malfunctionOrDiagnostic}`
                        )}
                      </Typography>
                      <Typography variant="caption">
                        {translate(
                          `resources.hos-eld-malfunctions.malfunctionDescriptions.${record.malfunctionOrDiagnostic}`
                        )}
                      </Typography>
                    </Box>
                  ) : null
                }
              />
              <BooleanField
                headerClassName="centered-content"
                className={classNames(
                  classes.centeredContent,
                  "centered-content"
                )}
                source="isCleared"
                label="resources.hos-eld-malfunctions.labels.isCleared"
              />
            </Datagrid>
          }
        />
      </DefaultProLogsList>
    </div>
  );
};
